/**
 * WCAG 2.1 Contrast Evaluation Utilities
 *
 * Provides contrast ratio calculation and AA/AAA compliance checking
 * built on top of the colorHelpers luminance function.
 *
 * @module wcag
 */

import { getColorLuminance, hexToHsl, hslToHex } from './colorHelpers';

/**
 * Calculate the WCAG 2.1 contrast ratio between two hex colors.
 *
 * Formula: (L1 + 0.05) / (L2 + 0.05)
 * where L1 is the lighter color's luminance and L2 is the darker.
 *
 * @param hex1 - First color in hex format (#RRGGBB)
 * @param hex2 - Second color in hex format (#RRGGBB)
 * @returns Contrast ratio between 1.0 (identical) and 21.0 (black/white)
 */
export function getContrastRatio(hex1: string, hex2: string): number {
  const L1 = getColorLuminance(hex1);
  const L2 = getColorLuminance(hex2);

  const lighter = Math.max(L1, L2);
  const darker = Math.min(L1, L2);

  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Check if a contrast ratio meets WCAG AA requirements.
 *
 * - Normal text: ratio >= 4.5:1
 * - Large text (18pt+ or 14pt+ bold): ratio >= 3:1
 *
 * @param ratio - Contrast ratio to check
 * @param isLargeText - Whether the text is considered "large" per WCAG
 * @returns true if the ratio meets AA requirements
 */
export function meetsAA(ratio: number, isLargeText: boolean = false): boolean {
  return isLargeText ? ratio >= 3.0 : ratio >= 4.5;
}

/**
 * Check if a contrast ratio meets WCAG AAA requirements.
 *
 * - Normal text: ratio >= 7:1
 * - Large text (18pt+ or 14pt+ bold): ratio >= 4.5:1
 *
 * @param ratio - Contrast ratio to check
 * @param isLargeText - Whether the text is considered "large" per WCAG
 * @returns true if the ratio meets AAA requirements
 */
export function meetsAAA(ratio: number, isLargeText: boolean = false): boolean {
  return isLargeText ? ratio >= 4.5 : ratio >= 7.0;
}

/**
 * Get the highest WCAG compliance level achieved by two colors.
 *
 * @param hex1 - First color in hex format (#RRGGBB)
 * @param hex2 - Second color in hex format (#RRGGBB)
 * @param isLargeText - Whether the text is considered "large" per WCAG
 * @returns 'AAA' if meets 7:1+, 'AA' if meets 4.5:1+, 'fail' otherwise
 */
export function getComplianceLevel(
  hex1: string,
  hex2: string,
  isLargeText: boolean = false
): 'AAA' | 'AA' | 'fail' {
  const ratio = getContrastRatio(hex1, hex2);

  if (meetsAAA(ratio, isLargeText)) return 'AAA';
  if (meetsAA(ratio, isLargeText)) return 'AA';
  return 'fail';
}

/**
 * Suggest a passing foreground color by adjusting lightness while
 * preserving the original hue and saturation.
 *
 * Uses binary search over lightness to find the closest color to the
 * original that meets the target contrast ratio against the background.
 *
 * @param fg - Current foreground color in hex (#RRGGBB)
 * @param bg - Background color in hex (#RRGGBB)
 * @param target - Target contrast ratio (default 4.5 for AA normal text)
 * @returns Suggested hex color, or null if no solution exists
 */
export function suggestPassingColor(
  fg: string,
  bg: string,
  target: number = 4.5,
): string | null {
  const fgHsl = hexToHsl(fg);
  if (!fgHsl) return null;

  const bgLum = getColorLuminance(bg);

  // Try both directions: darker and lighter
  const tryDirection = (low: number, high: number): string | null => {
    for (let i = 0; i < 30; i++) {
      const mid = (low + high) / 2;
      const candidate = hslToHex(fgHsl.h, fgHsl.s, mid);
      const ratio = getContrastRatio(candidate, bg);

      if (Math.abs(ratio - target) < 0.05) {
        return candidate;
      }

      // If bg is light, we need a darker fg (lower lightness → higher contrast)
      // If bg is dark, we need a lighter fg (higher lightness → higher contrast)
      if (bgLum > 0.5) {
        // Light background: lower lightness = more contrast
        if (ratio < target) {
          high = mid;
        } else {
          low = mid;
        }
      } else {
        // Dark background: higher lightness = more contrast
        if (ratio < target) {
          low = mid;
        } else {
          high = mid;
        }
      }
    }

    // Check if the converged value meets the target
    const final = hslToHex(fgHsl.h, fgHsl.s, (low + high) / 2);
    return getContrastRatio(final, bg) >= target ? final : null;
  };

  // First try adjusting in the direction that increases contrast
  if (bgLum > 0.5) {
    // Light bg: try going darker first, then lighter
    return tryDirection(0, fgHsl.l) ?? tryDirection(fgHsl.l, 1);
  } else {
    // Dark bg: try going lighter first, then darker
    return tryDirection(fgHsl.l, 1) ?? tryDirection(0, fgHsl.l);
  }
}
